// Small non-interactive map that renders a single zone polygon + neighborhood
// context. Used on the Zone Detail page and anywhere we want a visual anchor
// without the full Course Map chrome.

import { useEffect, useRef } from 'react';
import mapboxgl, { type Map as MapboxMap } from 'mapbox-gl';
import type { Zone } from '@greeniq/shared';
import { ZONE_TYPE_STYLE } from '../lib/zoneStyle.js';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN ?? '';

function bboxOf(poly: GeoJSON.Polygon | GeoJSON.MultiPolygon): [number, number, number, number] {
  const rings = poly.type === 'Polygon' ? poly.coordinates : poly.coordinates.flat();
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const ring of rings) {
    for (const [lng, lat] of ring as [number, number][]) {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
  }
  return [minLng, minLat, maxLng, maxLat];
}

export function ZoneMiniMap({ zone }: { zone: Zone }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapboxMap | null>(null);

  useEffect(() => {
    if (!containerRef.current || !MAPBOX_TOKEN || !zone.polygon) return;
    if (mapRef.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const bbox = bboxOf(zone.polygon);
    const style = ZONE_TYPE_STYLE[zone.zoneType];

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/satellite-v9',
      bounds: [
        [bbox[0], bbox[1]],
        [bbox[2], bbox[3]],
      ],
      fitBoundsOptions: { padding: 30, duration: 0 },
      interactive: false,
    });
    mapRef.current = map;

    map.on('load', () => {
      map.addSource('z', {
        type: 'geojson',
        data: { type: 'Feature', properties: {}, geometry: zone.polygon },
      });
      map.addLayer({
        id: 'z-fill',
        source: 'z',
        type: 'fill',
        paint: { 'fill-color': style.fill, 'fill-opacity': 0.5 },
      });
      map.addLayer({
        id: 'z-line',
        source: 'z',
        type: 'line',
        paint: { 'line-color': style.outline, 'line-width': 2.5 },
      });
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [zone]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className="aspect-video rounded-lg bg-bg-elevated border border-border flex items-center justify-center text-xs text-fg-muted">
        Map unavailable (VITE_MAPBOX_TOKEN not set)
      </div>
    );
  }
  return <div ref={containerRef} className="aspect-video rounded-lg overflow-hidden bg-bg-elevated" />;
}
